/**
 * @fileoverview Shared billing admin components (MOR-438)
 *
 * The visual language every billing surface speaks: plan / charge-status /
 * Stripe-status pills on the Badge colour system (so "paid" is the same green
 * everywhere), stat tiles for headline numbers, and the rate-schedule
 * timeline that replaces the old "25% → 25% → 10%" string.
 *
 * Pure helpers live in `billingFormat.ts` — mixing component and
 * non-component exports breaks React Fast Refresh.
 */

import type { ReactNode } from "react";
import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";
import {
  chargeStatusMeta,
  PLAN_META,
  STRIPE_STATUS_COLORS,
  type BillingPlan,
  type RateSchedule,
} from "./billingFormat";

// ---------------------------------------------------------------------------
// Pills
// ---------------------------------------------------------------------------

export function PlanBadge({ plan }: { plan: BillingPlan }) {
  const meta = PLAN_META[plan];
  return (
    <Badge color={meta.color} variant="soft" size="sm">
      {meta.label}
    </Badge>
  );
}

/** Status pill with a colour dot — the only way charge state is rendered. */
export function ChargeStatusPill({ status }: { status: string }) {
  const meta = chargeStatusMeta(status);
  return (
    <Badge
      color={meta.color}
      variant="soft"
      size="sm"
      className="gap-1.5 whitespace-nowrap"
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", meta.barClass)} />
      {meta.label}
    </Badge>
  );
}

export function StripeStatusPill({
  stripeCustomerId,
  stripeSubscriptionStatus,
}: {
  stripeCustomerId: string | null;
  stripeSubscriptionStatus: string | null;
}) {
  if (!stripeCustomerId) {
    return <span className="text-xs text-muted-foreground">Not connected</span>;
  }
  if (!stripeSubscriptionStatus) {
    return (
      <Badge color="gray" variant="outline" size="sm">
        Customer only
      </Badge>
    );
  }
  const color = STRIPE_STATUS_COLORS[stripeSubscriptionStatus] ?? "gray";
  return (
    <Badge
      color={color}
      variant="soft"
      size="sm"
      className="gap-1.5 whitespace-nowrap"
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          color === "emerald"
            ? "bg-emerald-500"
            : color === "red"
              ? "bg-red-500"
              : color === "sky"
                ? "bg-sky-500"
                : color === "amber"
                  ? "bg-amber-400"
                  : "bg-gray-400",
        )}
      />
      {stripeSubscriptionStatus.replace(/_/g, " ")}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Stat tiles
// ---------------------------------------------------------------------------

export function StatTile({
  label,
  value,
  hint,
  tone = "default",
  icon,
  children,
}: {
  label: string;
  value: string;
  hint?: ReactNode;
  tone?: "default" | "warning" | "positive";
  icon?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        {icon && <span className="text-muted-foreground/60">{icon}</span>}
      </div>
      <span
        className={cn(
          "text-2xl font-semibold tabular-nums leading-none tracking-tight",
          tone === "warning" && "text-destructive",
          tone === "positive" && "text-emerald-600 dark:text-emerald-400",
        )}
      >
        {value}
      </span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rate schedule timeline
// ---------------------------------------------------------------------------

/**
 * The declining fee schedule as a segmented timeline instead of an arrow
 * string: one cell per period, rate large, month range small.
 */
export function RateScheduleTimeline({
  schedule,
  className,
}: {
  schedule: RateSchedule;
  className?: string;
}) {
  if (schedule.length === 0) {
    return (
      <span className="text-sm text-muted-foreground">No rate periods.</span>
    );
  }
  return (
    <div className={cn("flex flex-wrap items-stretch gap-1", className)}>
      {schedule.map((p, i) => (
        <div
          key={i}
          className="flex min-w-[4.5rem] flex-1 flex-col items-center gap-0.5 rounded-md border bg-muted/40 px-2 py-1.5"
        >
          <span className="text-sm font-semibold tabular-nums leading-none">
            {p.ratePercent}%
          </span>
          <span className="text-[10px] tabular-nums text-muted-foreground">
            {p.toMonth === undefined
              ? `${p.fromMonth}m+`
              : `${p.fromMonth}–${p.toMonth}m`}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export function EmptyState({
  icon,
  title,
  hint,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5 py-10 text-center">
      {icon && <span className="text-muted-foreground/40">{icon}</span>}
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      {hint && <p className="text-xs text-muted-foreground/70">{hint}</p>}
    </div>
  );
}
